import DOMPurify from 'dompurify'
import formatString from 'utils/formatString'
import IconRenderer, { Icon } from './IconRenderer'
import CustomLink from 'designsystem/CustomLink'
import { Card } from 'designsystem/Card'

interface JobsListProps extends React.HTMLAttributes<HTMLUListElement> {
  jobs: GreenhouseJobOutput[]
  departments: GreenhouseEveryDepartment
}

const JobsList = ({
  jobs,
  departments,
  ...props
}: JobsListProps): JSX.Element | null => {
  return departments.length > 0 ? (
    <ul aria-label='Jobs list' {...props}>
      {departments.map((department) => {
        const { id, name, jobs: departmentJobs } = department
        const formattedDepartmentName = formatString(name)

        return (
          departmentJobs.length > 0 && (
            <li
              key={id}
              id={`${formattedDepartmentName}-jobs`}
              className='spacer-2xl-padding md:flex md:flex-col md:items-center lg:grid lg:grid-cols-2 '
            >
              {/* Department info */}
              <div className='lg:relative lg:h-full'>
                <div className='flex flex-col items-start md:items-center lg:items-start lg:sticky lg:mb-20 lg:top-20 lg:left-0 lg:mr-30 2xl:mr-40'>
                  <IconRenderer
                    icon={formattedDepartmentName as Icon}
                    className='h-12 w-auto mb-4 text-blue'
                  />

                  <h3>{name}</h3>

                  {departmentsDescriptions[formattedDepartmentName] && (
                    <p className='spacer-3xs large-body-text opacity-60 md:text-center lg:text-left'>
                      {departmentsDescriptions[formattedDepartmentName]}
                    </p>
                  )}

                  {/* {thingsYouCanExpect.length > 0 && (
                    <div className='spacer-3xs'>
                      <ul
                        aria-label='Things you can expect'
                        className='flex flex-wrap -mt-2'
                      >
                        {thingsYouCanExpect.map((thing, index) => (
                          <li
                            key={`things-you-can-expect-${index}`}
                            className='flex items-center mt-2'
                          >
                            {index !== 0 && (
                              <div className='h-1.5 w-1.5 bg-neutral-25 rounded-full mr-4' />
                            )}
                            <p className='font-demibold opacity-60 mr-4'>
                              {thing}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )} */}
                </div>
              </div>

              {/* Department job list */}
              <ul className='spacer-xl lg:mt-0 grid gap-8 w-full'>
                {departmentJobs.map((job) => {
                  const { id, title, absolute_url } = job
                  const description = jobs.find(
                    (job) => job.id === id,
                  )?.description

                  return (
                    <li key={id}>
                      <a href={absolute_url} target='_blank' rel='noreferrer'>
                        <Card hoverable noMaxWidth noPadding className='p-8'>
                          <div className='flex items-center'>
                            <IconRenderer
                              icon={formattedDepartmentName as Icon}
                              className='h-8 w-auto mr-4 text-blue'
                            />
                            <h4 className='h5-size'>{title}</h4>
                          </div>

                          {description && description.kind === 'iframe' ? (
                            <div
                              className='spacer-3xs job-video-iframe'
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(description.html, {
                                  ADD_TAGS: ['iframe'],
                                  ADD_ATTR: [
                                    'allow',
                                    'allowfullscreen',
                                    'frameborder',
                                    'scrolling',
                                  ],
                                }),
                              }}
                            />
                          ) : description ? (
                            <div
                              className='spacer-3xs opacity-60 max-3-lines'
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(description.html),
                              }}
                            />
                          ) : null}

                          <CustomLink className='spacer-3xs' text='Apply now' />
                        </Card>
                      </a>
                    </li>
                  )
                })}
              </ul>
            </li>
          )
        )
      })}
    </ul>
  ) : null
}

export default JobsList

const departmentsDescriptions = {
  'customer-success':
    'At Stellate, we really care about our users and customers. We measure our success in our customers being successful with our solutions.',
  design: `Ultimately, we solve problems for humans. In order to fully leverage the potential of the tech we build, we need strong design.`,
  engineering:
    'Stellate tackles many hard problems in the GraphQL space, which need novel solutions.',
  marketing:
    'Having the best product possible is not enough. The world needs to know about it.',
  'developer-relations': `We are building tools from engineers for engineers. A good relationship with the developer community is crucial for our success.`,
  'people-&-operations':
    'A company is a group of people. To make sure we can maintain and grow a healthy team and culture, the people and operations team plays a crucial role.',
  product:
    'We as a company need to ensure, that we build features, that the world actually needs. Problems over solutions.',
  sales:
    'Since Stellate got released in June 2021, we experienced a pull from enterprises. Sales is crucial to enable more people to benefit from our solutions.',
}

// const thingsYouCanExpect = ['Empowerment', 'Distributed', 'One job, one pay']
